<template>
  <div v-if="show" class="modal-overlay">
    <div class="absolute inset-0 bg-black opacity-50 modal-wrapper" @click="closeAlert"></div>
    <div class="modal-content">
      <!-- Header -->
      <div class="modal-header">
        <div class="flex items-center">
          <div class="flex-shrink-0">
            <!-- Success Icon -->
            <div v-if="type === 'success'" class="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
              <svg class="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
            <!-- Error Icon -->
            <div v-else-if="type === 'error'" class="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
              <svg class="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </div>
            <!-- Warning Icon -->
            <div v-else-if="type === 'warning'"
              class="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
              <svg class="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L5.268 16.5c-.77.833.192 2.5 1.732 2.5z">
                </path>
              </svg>
            </div>
            <!-- Info Icon -->
            <div v-else class="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
              <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
          </div>
          <div class="ml-3 flex-1">
            <h3 class="text-section-header">{{ title }}</h3>
          </div>
          <button @click="closeAlert" class="text-gray-400 hover:text-gray-600 transition-colors">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
      </div>

      <!-- Message -->
      <div class="modal-body">
        <p class="text-body">{{ message }}</p>
      </div>

      <!-- Actions -->
      <div class="modal-footer">
        <button v-if="showCancel" @click="onCancel" class="btn-secondary flex-1">
          {{ cancelText }}
        </button>
        <button @click="onConfirm" :class="[
          'flex-1',
          type === 'error' ? 'bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-medium focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-all duration-200 cursor-pointer' :
            type === 'warning' ? 'bg-yellow-600 hover:bg-yellow-700 text-white px-6 py-3 rounded-lg font-medium focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 transition-all duration-200 cursor-pointer' :
              type === 'success' ? 'btn-primary' :
                'bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 cursor-pointer'
        ]">
          {{ confirmText }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useBodyScrollLock } from '../composables/useBodyScrollLock'

interface Props {
  show: boolean
  type: 'success' | 'error' | 'warning' | 'info'
  title: string
  message: string
  confirmText?: string
  cancelText?: string
  showCancel?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  confirmText: 'OK',
  cancelText: 'Cancel',
  showCancel: false
})
useBodyScrollLock(() => props.show)

const emit = defineEmits(['confirm', 'cancel', 'close'])

const closeAlert = () => {
  emit('close')
}

const onConfirm = () => {
  emit('confirm')
  closeAlert()
}

const onCancel = () => {
  emit('cancel')
  closeAlert()
}
</script>
